import React from 'react';
import { useRoaryImport } from '../../../hooks/useRoaryImport';
import { ImportLayoutStep } from './ImportLayoutStep';
import { ImportUrlStep } from './ImportUrlStep';
import { ImportScanProgress } from './ImportScanProgress';
import { ImportPlanReview } from './ImportPlanReview';
import { ImportBuildProgress } from './ImportBuildProgress';
import { ImportComplete } from './ImportComplete';

interface RoaryImportProps {
  onClose: () => void;
}

/**
 * Top-level container for the Import Website mode. Phase-driven router that
 * renders one of five sub-views. State lives in `useRoaryImport`.
 */
export const RoaryImport: React.FC<RoaryImportProps> = ({ onClose }) => {
  const {
    state,
    selectLayout,
    backToLayout,
    startImport,
    confirmPages,
    confirmPlan,
    cancel,
    cancelInFlight,
    cancelConflictAndRetry,
    reset,
  } = useRoaryImport();

  if (state.phase === 'picking_layout') {
    return <ImportLayoutStep onSelect={selectLayout} onClose={onClose} />;
  }

  if (state.phase === 'idle' || state.phase === 'creating' || state.phase === 'failed') {
    return (
      <ImportUrlStep
        onSubmit={startImport}
        onClose={onClose}
        onBack={backToLayout}
        layoutName={state.layoutTemplateName}
        loading={state.phase === 'creating'}
        error={state.error}
        conflict={state.conflict}
        onCancelInFlight={cancelInFlight}
        onCancelAndRetry={cancelConflictAndRetry}
      />
    );
  }

  if (
    state.phase === 'scanning' ||
    state.phase === 'analyzing' ||
    state.phase === 'awaiting_page_selection'
  ) {
    return (
      <ImportScanProgress
        sourceUrl={state.sourceUrl ?? ''}
        pages={[...state.pages.values()]}
        assetsDiscovered={state.assetsDiscovered}
        brandColors={state.brandColors}
        brandFonts={state.brandFonts}
        logoUrl={state.logoUrl}
        analyzing={state.phase === 'analyzing'}
        selecting={state.phase === 'awaiting_page_selection'}
        onCancel={cancel}
        onContinue={confirmPages}
      />
    );
  }

  if (state.phase === 'awaiting_confirmation' && state.plan) {
    return (
      <ImportPlanReview
        plan={state.plan}
        warnings={state.warnings}
        onConfirm={confirmPlan}
        onCancel={cancel}
      />
    );
  }

  if (state.phase === 'building') {
    return (
      <ImportBuildProgress
        progress={state.buildProgress}
        builtSiteId={state.builtSiteId}
        onCancel={cancel}
      />
    );
  }

  if (state.phase === 'completed') {
    return (
      <ImportComplete
        siteId={state.builtSiteId}
        pageCount={state.builtPageIds.length}
        onAnotherImport={reset}
        onClose={onClose}
      />
    );
  }

  return null;
};
